import { useState, useEffect, useMemo } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import mixpanel from 'mixpanel-browser'
import './App.css'
import LoginPage from './components/LoginPage'
import DashboardLayout from './components/layout/DashboardLayout'
import { getToken, setToken, clearToken, fetchAndStoreUserContext, fetchAndStoreCurrentUser, getPermissions, setPermissions, setUserName, type LoginData } from './services/auth'
import { PermissionsContext } from './contexts/PermissionsContext'

/** Redirects unauthenticated users to /login. Re-checks localStorage on every render. */
function PrivateRoute({ children }: { children: React.ReactNode }) {
  return getToken() ? <>{children}</> : <Navigate to="/login" replace />;
}

function App() {
  const [authToken, setAuthToken] = useState<string | null>(getToken());
  const [permissions, setPermissionsState] = useState<string[]>(() => getPermissions());

  // Fetch org/school/user context on load when already authenticated (run once on mount)
  useEffect(() => {
    if (getToken()) {
      fetchAndStoreUserContext();
      fetchAndStoreCurrentUser();
    }
  }, []);

  const handleLogin = (data: LoginData, email: string) => {
    setToken(data.token);
    setPermissions(data.permissions);
    if (data.name) setUserName(data.name);
    setAuthToken(data.token);
    setPermissionsState(data.permissions);
    fetchAndStoreUserContext();
    if (!data.name) fetchAndStoreCurrentUser();

    mixpanel.identify(email);
    mixpanel.people.set({
      $name: data.name,
      $email: email,
      role: data.role,
    });
  };

  const handleLogout = () => {
    clearToken();
    setAuthToken(null);
    setPermissionsState([]);
  };

  const permissionsValue = useMemo(() => ({
    permissions,
    hasPermission: (perm: string) => permissions.includes(perm),
    hasAnyPermission: (perms: string[]) => perms.some(p => permissions.includes(p)),
  }), [permissions]);

  return (
    <Routes>
      {/* Public route — redirect to dashboard if already logged in */}
      <Route
        path="/login"
        element={
          authToken
            ? <Navigate to="/dashboard" replace />
            : <LoginPage onLogin={handleLogin} />
        }
      />

      {/* Protected routes — redirect to /login if not authenticated */}
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <PermissionsContext.Provider value={permissionsValue}>
              <DashboardLayout onLogout={handleLogout} />
            </PermissionsContext.Provider>
          </PrivateRoute>
        }
      />

      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App

